import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import { LogisticsOptions, onForward, OrderTypeOptions, PaymentModeOptions, ServiceTypeOptions } from './constants';
import * as Yup from 'yup';
import { getCarrierSettings } from '@Reducers/User/actions';
import Loader from '@Components/Loader';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import {
  getWarehouses,
  addWarehouse,
} from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Thinklogistics/constants';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';

// ------- VALIDATION SCHEMA -------
const iThinkLogisticsInformationSchema = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  logistics: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Logistics is required'),
  order_type: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Order type is required'),
  payment_mode: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Payment mode is required'),
  pickup_address_id: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Warehouse address is required'),
  s_type: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Service Type is required'),
});

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const UserIThinkLogisticsDomestic = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isWarehouseLoading, setIsWarehouseLoading] = useState<boolean>(false);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userIThinkLogisticsInformationDomesticDetails: iThinkLogisticsDomesticSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);
  const { iThinkLogisticsDomesticDetails } = useSelector((state: RootStateType) => state.adminShipment);

  // Hardcoded "Add Warehouse" option
  const addWarehouseOption: DropdownOptionType = {
    value: 'add_warehouse',
    display: '+ Add Warehouse',
  };

  // Function to get warehouse options based on iThinkLogisticsDomesticSettings
  const getWarehouseOptions = () => {
    // If iThinkLogisticsDomesticSettings has pickup_address_id with length, include add warehouse option
    if (iThinkLogisticsDomesticSettings && iThinkLogisticsDomesticSettings?.pickup_address_id?.length > 0) {
      return [addWarehouseOption, ...iThinkLogisticsDomesticSettings.pickup_address_id];
    }

    // Otherwise use the combined options with fetched warehouses
    return [addWarehouseOption, ...pickupAddressOptions];
  };

  const fetchWarehouses = async (accountNumber: string) => {
    try {
      const warehouses = await getWarehouses(accountNumber);
      setPickupAddressOptions(warehouses);
    } catch (error) {
      console.error('Failed to fetch warehouses', error);
    }
  };

  const handleWarehouseChange = async (value: string[]) => {
    const selectedValue = value[0] || '';

    if (selectedValue === 'add_warehouse') {
      // Handle "Add Warehouse" option click
      if (!values.accountNo || (Array.isArray(values.accountNo) && values.accountNo.length === 0)) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: 'Please select an iThink Logistics Account first',
          }),
        );
        setFieldValue('pickup_address_id', '');
        return;
      }

      setIsWarehouseLoading(true);
      try {
        // Extract account number from the selected account
        let accountNumber = '';
        if (Array.isArray(values.accountNo)) {
          // When accountNo is an array of objects with value property
          const accountValue = values.accountNo[0]?.value || '';
          accountNumber = accountValue.split('-')[0];
        } else {
          // When accountNo is a string
          accountNumber = values.accountNo.split('-')[0];
        }

        const shipmentNumber = shipmentById?.shipmentNumber || '';
        await addWarehouse({ accountNo: accountNumber, shipmentNumber });

        // Refresh the warehouses list after successful addition
        await fetchWarehouses(accountNumber);

        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success!',
            subTitle: 'Warehouse added successfully!',
          }),
        );
      } catch (error) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Error',
            subTitle: error?.response?.data?.msg || 'Failed to add warehouse. Please try again.',
          }),
        );
        console.error('Error adding warehouse:', error);
      } finally {
        setIsWarehouseLoading(false);
        setFieldValue('pickup_address_id', '');
      }
    } else {
      // Normal warehouse selection
      setFieldValue('pickup_address_id', selectedValue);
    }
  };

  // ------------ FORMIK ------------
  const { values, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      invoiceNumber: invoiceNo,
      accountNo: iThinkLogisticsDomesticSettings?.accountNo
        ? iThinkLogisticsDomesticSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
      ...initialStates,
    },
    validationSchema: iThinkLogisticsInformationSchema,
    onSubmit(values) {
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
        setLoading,
      });
    },
  });

  const accountNumber = (values?.accountNo?.[0]?.value ?? '').split('-')[0];

  useEffect(() => {
    if (accountNumber && shipmentById?.buyingPricing?.code === 'ithink_logistics_domestic') {
      fetchWarehouses(accountNumber);
    }
  }, [shipmentById, accountNumber]);

  useEffect(() => {
    if (shipmentById?._accountId.accountNumber) {
      dispatch(
        getCarrierSettings({
          csp: 'ithink_logistics_domestic',
          accountNumber: shipmentById?._accountId.accountNumber,
        }),
      );
    }
  }, [shipmentById?._accountId.accountNumber]);

  return (
    <>
      <Loader overly loading={loading}></Loader>
      {showFedExDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">iThink Logistics Domestic Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="iThink Logistics Domestic Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={iThinkLogisticsDomesticSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable || isWarehouseLoading}
                    id="pickup_address_id"
                    label="Warehouse Address"
                    required
                    placeholder="Select"
                    name="pickup_address_id"
                    value={values.pickup_address_id ? [values.pickup_address_id] : []}
                    options={getWarehouseOptions()}
                    onChange={handleWarehouseChange}
                    error={errors.pickup_address_id as string}
                  />
                  {isWarehouseLoading && (
                    <Typography variant="caption" sx={{ color: 'primary.main', mt: 1, display: 'block' }}>
                      Adding warehouse, please wait...
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="logistics"
                    label="Logistics"
                    required
                    placeholder="Select"
                    name="logistics"
                    value={values.logistics ? [values.logistics] : []}
                    options={
                      iThinkLogisticsDomesticSettings?.logistics?.length
                        ? iThinkLogisticsDomesticSettings.logistics
                        : LogisticsOptions
                    }
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('logistics', selected);
                    }}
                    error={errors.logistics as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="order_type"
                    label="Order Type"
                    required
                    placeholder="Select"
                    name="order_type"
                    value={values.order_type ? [values.order_type] : []}
                    options={
                      iThinkLogisticsDomesticSettings?.order_type?.length
                        ? iThinkLogisticsDomesticSettings.order_type
                        : OrderTypeOptions
                    }
                    onChange={val => setFieldValue('order_type', val[0] || '')}
                    error={errors.order_type as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="payment_mode"
                    label="Payment Mode"
                    required
                    placeholder="Select"
                    name="payment_mode"
                    value={values.payment_mode ? [values.payment_mode] : []}
                    options={
                      iThinkLogisticsDomesticSettings?.payment_mode?.length
                        ? iThinkLogisticsDomesticSettings.payment_mode
                        : PaymentModeOptions
                    }
                    onChange={val => setFieldValue('payment_mode', val[0] || '')}
                    error={errors.payment_mode as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="s_type"
                    label="Service Type"
                    required
                    placeholder="Select"
                    name="s_type"
                    value={values.s_type ? [values.s_type] : []}
                    options={
                      iThinkLogisticsDomesticSettings?.s_type?.length
                        ? iThinkLogisticsDomesticSettings.s_type
                        : ServiceTypeOptions
                    }
                    onChange={val => setFieldValue('s_type', val[0] || '')}
                    error={errors.s_type as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable || isWarehouseLoading}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {iThinkLogisticsDomesticDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserIThinkLogisticsDomestic;
